<!-- Карточка кейса - Molecule -->
<template>
    <article class="case-card" @click="handleClick">
        <!-- Контейнер для изображения -->
        <div class="case-card__image"></div>

        <!-- Контент карточки -->
        <div class="case-card__content flex-end">
            <!-- Label -->
            <span class="case-card__label">
                {{ label }}
            </span>

            <!-- Имя артиста -->
            <h3 class="case-card__name">
                {{ artistName }}
            </h3>

            <!-- Описание (показывается при hover) -->
            <p v-if="description" class="case-card__description">
                {{ description }}
            </p>

            <!-- Кнопка (показывается при hover) -->
            <button class="case-card__button">
                Читать историю
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
            </button>
        </div>
    </article>
</template>

<script setup lang="ts">
interface Props {
    /** Имя артиста */
    artistName: string
    /** Label карточки (по умолчанию "артист Touch") */
    label?: string
    /** Описание достижений */
    description?: string
}

withDefaults(defineProps<Props>(), {
    label: 'артист Touch'
})

const emit = defineEmits<{
    click: []
}>()

const handleClick = () => {
    emit('click')
}
</script>

<style scoped>
.case-card {
    width: 100%;
    height: 360px;
    border-radius: 30px;
    background-image: url('/images/block/artist/iavatar.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    cursor: pointer;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    position: relative;
}

.case-card::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 30px;
    background-image: url('/images/block/artist/active.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0;
    transition: opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    pointer-events: none;
}

.case-card:hover::after {
    opacity: 1;
    will-change: opacity;
    /* GPU ускорение только при hover */
}

/* Мобильная версия - активная картинка всегда показана */
@media (max-width: 1023px) {
    .case-card::after {
        opacity: 1;
    }
}

.case-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.1);
    will-change: transform, box-shadow;
    /* GPU ускорение только при hover */
}

.case-card__image {
    width: 100%;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    z-index: 2;
}

.case-card__content {
    flex: 1;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 0.75rem;
    position: relative;
    z-index: 3;
}

.case-card__label {
    display: inline-flex;
    padding: 6px 13px;
    align-items: center;
    gap: 6px;
    color: rgba(236, 236, 236, 0.6);
    border-radius: 15px;
    background: #000;
    width: fit-content;
    border: 0.001rem solid #FFF;
}

.case-card__name {
    font-family: 'Halvar Breitschrift', sans-serif;
    font-size: 24px;
    font-weight: 500;
    line-height: 1.1;
    color: #ececec;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.case-card__description {
    font-family: 'Halvar Engschrift', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: #ececec;
    margin: 0;
    opacity: 0;
    max-height: 0;
    overflow: hidden;
    transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), 
                max-height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.case-card:hover .case-card__description {
    opacity: 1;
    max-height: 100px;
}

/* Мобильная версия - описание всегда видно */
@media (max-width: 1023px) {
    .case-card__description {
        opacity: 1;
        max-height: 100px;
    }
}

.case-card__button {
    display: flex;
    padding: 15px 20px;
    align-items: center;
    gap: 6px;
    border-radius: 15px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    background: linear-gradient(90deg, #000 4.2%, #181818 53.14%, #000 100.23%), #FFF;
    color: #ececec;
    font-family: 'Halvar Engschrift', sans-serif;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    width: fit-content;
    opacity: 0;
    max-height: 0;
    overflow: hidden;
    transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), 
                max-height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.case-card:hover .case-card__button {
    opacity: 1;
    max-height: 60px;
}

/* Мобильная версия - кнопка всегда видна */
@media (max-width: 1023px) {
    .case-card__button {
        opacity: 1;
        max-height: 60px;
    }
}

.case-card__button svg {
    flex-shrink: 0;
}
</style>
